'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDebounce } from 'use-debounce';
import { useSearchAddressesQuery as useSearchAddresses } from 'src/api/redux';
import { AddressType } from './types';
import {
  Typography,
  CircularProgress,
  Search,
} from 'src/components/PulseUI';

interface DataIssueAddressSearchProps {
  setAddressData: (value: AddressType | null) => void;
}

const DataIssueAddressSearch = ({
  setAddressData,
}: DataIssueAddressSearchProps) => {
  const [open, setOpen] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [debouncedUserInput] = useDebounce(userInput, 500);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const isSelectingRef = useRef(false);

  const isActiveSearch = debouncedUserInput.length > 2;

  const { data, isLoading, error } = useSearchAddresses(
    {
      search: debouncedUserInput,
    },
    {
      skip: !isActiveSearch,
    }
  );

  const displayOptions = ((): AddressType[] => {
    if (
      !isLoading &&
      !error &&
      isActiveSearch &&
      data?.results &&
      Array.isArray(data.results)
    ) {
      return data.results as AddressType[];
    }

    return [];
  })();

  const handleOptionSelect = (option: AddressType) => {
    isSelectingRef.current = true;
    setAddressData(option);
    setUserInput(option.address_line_1 || '');
    setOpen(false);
  };

  const handleSearch = (value: string) => {
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }
    setUserInput(value);
    setAddressData(null);
    if (value.length > 0 && !open) {
      setOpen(true);
    }
  };

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        inputWrapperRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const updateDropdownPosition = useCallback(() => {
    if (inputWrapperRef.current) {
      const rect = inputWrapperRef.current.getBoundingClientRect();
      const scrollX = window.scrollX || document.documentElement.scrollLeft;
      const scrollY = window.scrollY || document.documentElement.scrollTop;

      setPosition({
        top: rect.bottom + scrollY + 4,
        left: rect.left + scrollX,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (open) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
    }

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [open, updateDropdownPosition]);

  const getHelperText = () => {
    if (!isActiveSearch) return 'Start typing (i.e. 12 Main St)';
    if (isLoading) return 'Loading...';
    if (error) return 'Error fetching results';

    return '';
  };

  const helperText = getHelperText();
  const showDropdown =
    open && isActiveSearch && (isLoading || displayOptions.length > 0 || error);
  const showNoResults =
    isActiveSearch && !isLoading && !error && displayOptions.length === 0;

  return (
    <div
      className="flex flex-col gap-1"
      data-testid="data-issue-address-search"
    >
      <label
        htmlFor="address-search"
        className="text-sm font-medium text-gray-700"
      >
        Search for an existing address
      </label>
      <div ref={inputWrapperRef}>
          <Search
            id="address-search"
            placeholder="by Street Address"
            value={userInput}
            onSearch={handleSearch}
            onFocus={() => userInput.length > 0 && setOpen(true)}
            aria-label="Search for an existing address"
            data-testid="address-search-input"
            {...(isLoading && isActiveSearch
              ? { slotProps: { clearIcon: <CircularProgress size="sm" /> } }
              : {})}
            className="rounded-full"
          />
          {showDropdown &&
            createPortal(
              <ul
                ref={dropdownRef}
                role="listbox"
                aria-label="Address search results"
                data-testid="address-search-dropdown"
                className="fixed z-[9999] max-h-64 overflow-y-auto bg-white border border-neutral-200 rounded-lg shadow-lg"
                style={{
                  top: position.top,
                  left: position.left,
                  width: position.width,
                }}
              >
                {isLoading ? (
                  <li className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-600">
                    <CircularProgress size="sm" />
                    <span>Loading...</span>
                  </li>
                ) : error ? (
                  <li
                    className="px-3 py-2 text-sm text-red-600"
                    data-testid="address-search-error"
                  >
                    Error fetching results
                  </li>
                ) : (
                  displayOptions.map((option) => (
                    <li
                      key={option.address_key || `address-${Math.random()}`}
                      role="option"
                      aria-selected={false}
                      tabIndex={0}
                      className="flex flex-col gap-0.5 px-3 py-2 cursor-pointer hover:bg-neutral-100"
                      onClick={() => handleOptionSelect(option)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleOptionSelect(option);
                        }
                      }}
                      data-testid={`address-option-${option.address_key}`}
                    >
                      <Typography variant="body1">
                        {option.address_line_1}
                      </Typography>
                      {option.address_line_2 && (
                        <Typography variant="body2">
                          {option.address_line_2}
                        </Typography>
                      )}
                      {option.city && (
                        <Typography variant="caption" color="tertiary">
                          {option.city}, {option.state} {option.zip_code}
                        </Typography>
                      )}
                    </li>
                  ))
                )}
              </ul>,
              document.body
            )}
        </div>
      {(helperText || showNoResults) && (
        <Typography variant="caption" color="tertiary">
          {showNoResults ? 'No results found' : helperText}
        </Typography>
      )}
    </div>
  );
};

export { DataIssueAddressSearch };
